import PillarLayout from '../components/PillarLayout'

export default function FichajeDigital() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Fichaje digital 2026: métodos, ventajas y obligaciones legales",
    "description": "Guía sobre fichaje digital de empleados en España: métodos (app, QR, biometría, geolocalización), comparativa con métodos tradicionales y obligaciones del nuevo Real Decreto 2026.",
    "author": { "@type": "Organization", "name": "PresenzIA" },
    "publisher": { "@type": "Organization", "name": "PresenzIA", "url": "https://presenzia.app" },
    "datePublished": "2026-05-03",
    "dateModified": "2026-05-03",
    "mainEntityOfPage": "https://presenzia.app/fichaje-digital",
    "inLanguage": "es"
  }

  return (
    <PillarLayout
      title="Fichaje digital 2026: métodos, ventajas y obligaciones"
      description="Todo sobre el fichaje digital en España: métodos disponibles (app móvil, QR, geolocalización, biometría), comparativa con papel y Excel, y qué exige el nuevo Real Decreto 2026."
      canonicalPath="/fichaje-digital"
      jsonLd={jsonLd}
    >
      <h1>Fichaje digital 2026: métodos, ventajas y obligaciones</h1>
      <p className="lead">
        El fichaje digital sustituye a hojas de papel y planillas Excel para registrar la
        jornada laboral. En 2026 deja de ser una opción cómoda y pasa a ser, en la práctica,
        la única forma viable de cumplir la ley en una inspección de trabajo. Esta guía
        compara los métodos disponibles para que elijas el más adecuado.
      </p>

      <h2>¿Qué es el fichaje digital?</h2>
      <p>
        Llamamos fichaje digital al registro electrónico —con marca de tiempo del servidor—
        de la entrada, salida y pausas de cada trabajador. A diferencia de las soluciones
        clásicas, es <strong>inmutable, trazable y auditable</strong>: queda constancia de
        quién fichó, desde dónde, en qué momento, y cualquier modificación posterior queda
        registrada en log.
      </p>

      <h2>Métodos de fichaje digital disponibles</h2>

      <h3>1. App móvil con PIN o biometría</h3>
      <p>
        El método más extendido. El empleado abre la app de la empresa en su móvil y ficha
        con un PIN personal o con huella/reconocimiento facial. Permite fichar desde
        cualquier lugar. Ideal para personal en remoto, comerciales y servicios externos.
      </p>
      <p><strong>Pros:</strong> ubicuo, rápido, sin coste de hardware. <strong>Contras:</strong>
      requiere móvil del empleado o corporativo.</p>

      <h3>2. Web del empleado</h3>
      <p>
        El trabajador entra desde el navegador a su área personal y ficha con un clic.
        Útil para puestos de oficina con ordenador siempre encendido.
      </p>
      <p><strong>Pros:</strong> sin instalación, accesible. <strong>Contras:</strong>
      menos práctico para movilidad.</p>

      <h3>3. Quiosco compartido en el centro de trabajo</h3>
      <p>
        Una tablet o terminal fija junto a la entrada donde varios empleados fichan con su
        PIN personal. Habitual en talleres, almacenes, restaurantes y tiendas con plantilla
        sin móvil corporativo.
      </p>
      <p><strong>Pros:</strong> no requiere móvil de cada empleado. <strong>Contras:</strong>
      hay que invertir en una tablet por punto de fichaje.</p>

      <h3>4. Códigos QR físicos en el centro</h3>
      <p>
        Se imprimen QRs en zonas concretas (entrada, taller, sala de descanso). El empleado
        escanea con la app y ficha asociado a esa ubicación. Garantiza que el fichaje se
        produce realmente en el centro.
      </p>
      <p><strong>Pros:</strong> coste cero en hardware, validación de presencia.
      <strong>Contras:</strong> el QR debe ser dinámico para evitar fotos compartidas.</p>

      <h3>5. Geolocalización</h3>
      <p>
        El sistema captura las coordenadas GPS al fichar. Útil para empleados en ruta:
        comerciales, técnicos de mantenimiento, instaladores, transportistas. Requiere
        consentimiento expreso del trabajador (RGPD).
      </p>
      <p><strong>Pros:</strong> trazabilidad de campo. <strong>Contras:</strong>
      sensible en términos de privacidad.</p>

      <h3>6. Biometría (huella, reconocimiento facial)</h3>
      <p>
        Lectores biométricos físicos o reconocimiento facial vía cámara del móvil. Máxima
        identificación inequívoca. La AEPD exige base legal específica (no basta el
        consentimiento) y evaluación de impacto.
      </p>
      <p><strong>Pros:</strong> identidad infalsificable. <strong>Contras:</strong>
      complejidad legal y coste.</p>

      <h2>Tabla comparativa: papel vs Excel vs digital</h2>
      <table>
        <thead>
          <tr><th>Característica</th><th>Papel</th><th>Excel</th><th>Digital</th></tr>
        </thead>
        <tbody>
          <tr><td>Marca de tiempo confiable</td><td>❌</td><td>❌</td><td>✅</td></tr>
          <tr><td>Trazabilidad de cambios</td><td>❌</td><td>❌</td><td>✅</td></tr>
          <tr><td>Identificación inequívoca</td><td>⚠️</td><td>❌</td><td>✅</td></tr>
          <tr><td>Acceso del trabajador en tiempo real</td><td>❌</td><td>❌</td><td>✅</td></tr>
          <tr><td>Conservación 4 años garantizada</td><td>⚠️</td><td>⚠️</td><td>✅</td></tr>
          <tr><td>Aceptación ante la ITSS en 2026</td><td>❌</td><td>❌</td><td>✅</td></tr>
        </tbody>
      </table>

      <h2>Qué exige el nuevo Real Decreto 2026</h2>
      <p>
        El proyecto del Real Decreto sobre control horario digital obligatorio que el
        Ministerio de Trabajo está tramitando durante 2026 introduce, entre otros:
      </p>
      <ul>
        <li>Obligación de sistema digital en empresas con representación legal de trabajadores.</li>
        <li>Acceso remoto e instantáneo de la ITSS al registro (interoperabilidad).</li>
        <li>Marca de tiempo certificada (no manipulable por el empleador).</li>
        <li>Acceso del trabajador a su propio registro desde cualquier dispositivo.</li>
        <li>Registro de pausas, no sólo entrada/salida.</li>
      </ul>
      <p>
        Las empresas que ya tengan un sistema digital con auditoría no notarán cambios.
        Las que sigan con papel o Excel tendrán que migrar.
      </p>

      <h2>Ventajas operativas de fichar digitalmente</h2>
      <ul>
        <li><strong>Cero papeleo:</strong> los reportes se generan automáticamente.</li>
        <li><strong>Cálculo automático de horas extras</strong> y desviaciones de jornada.</li>
        <li><strong>Gestión integrada de ausencias y vacaciones</strong> sin emails ni planillas.</li>
        <li><strong>Notificaciones automáticas</strong> al manager si alguien falta o llega tarde.</li>
        <li><strong>Reportes para la asesoría laboral</strong> exportables en un clic.</li>
        <li><strong>Auditoría legal lista</strong> en caso de inspección.</li>
      </ul>

      <h2>Cómo elegir un software de fichaje digital</h2>
      <p>Lo que importa, en orden:</p>
      <ol>
        <li><strong>Trazabilidad real:</strong> que cada modificación quede registrada.</li>
        <li><strong>Servidores en la UE</strong> y RGPD compliant.</li>
        <li><strong>Multi-método:</strong> app, web, QR, quiosco — para que cubra toda la plantilla.</li>
        <li><strong>Reportes ITSS-ready</strong> sin tener que reformatear nada.</li>
        <li><strong>Precio claro y sin permanencia.</strong></li>
        <li><strong>Soporte en español.</strong></li>
      </ol>

      <p>
        PresenzIA cumple los seis puntos. Está diseñado para pymes españolas, con servidores
        en la UE, todos los métodos de fichaje incluidos en el plan Avanzado y reportes
        listos para la inspección. Empieza con 14 días gratis sin tarjeta.
      </p>
    </PillarLayout>
  )
}
